/**
 * Public landing page: what RumahKira does, a sample result, ways to start, pricing and FAQ.
 * Everything links straight into the app — no sign-up needed to try.
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { IconTile } from '../ui';
import BrandLogo from '../layout/BrandLogo';
import { ColorModeToggle, LanguageToggle } from '../layout/AppShell';
import HouseIllustration from '../estimate/HouseIllustration';
import { CATEGORY_ICONS, CATEGORY_ORDER, ALL_CALCULATORS } from '../calculators/calculatorMeta';
import { useLanguage } from '../../utils/LanguageContext';
import { useAuth } from '../../utils/authUtils';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { calculateQuickEstimate } from '../../utils/estimateEngine';
import { CATEGORIES } from '../../utils/materialEstimatorData';
import { formatCurrency, formatNumber } from '../../utils/format';
import { getPlanBenefits } from '../account/planBenefits';

const text = {
  en: {
    signIn: 'Sign in',
    openApp: 'Open app',
    heroTitle: 'Know what your house will cost — before you build',
    heroDesc: 'Estimate the construction cost and the materials you need for a kampung house, terrace or bungalow in Malaysia. In minutes, in English or Bahasa Malaysia.',
    startEstimate: 'Start free estimate',
    calculators: 'Material calculators',
    trust: ['Free to try', 'No sign-up needed', 'English & Bahasa Malaysia'],
    sampleLabel: 'Example estimate',
    sampleHouse: 'Terrace house · 1,400 sq ft · 1 storey · Standard',
    likely: 'Most likely',
    range: 'Range',
    materialsPreview: 'Includes a full material list',
    waysTitle: 'Three ways to plan your build',
    quickTitle: 'Quick estimate',
    quickDesc: 'Answer 4 questions — house type, size, storeys and finishing — and get a cost range with a budget breakdown.',
    detailedTitle: 'Detailed estimate',
    detailedDesc: 'Choose your walls, foundation, roof and ceiling, count rooms, doors and windows, and get a full bill of materials.',
    calcTitle: `${ALL_CALCULATORS.length} material calculators`,
    calcDesc: 'Bricks, cement, sand, tiles, concrete, roofing and more — in bags, pieces, m³ and tons you can order.',
    start: 'Start',
    browse: 'Browse',
    malaysiaTitle: 'Made for building in Malaysia',
    malaysiaDesc: 'House types, finishing levels and prices in RM that match how Malaysians build.',
    houseTypes: [
      ['kampung', 'Kampung house'],
      ['terrace', 'Terrace house'],
      ['bungalow', 'Bungalow'],
    ],
    categoriesTitle: 'Calculate materials for every part of the house',
    pricingTitle: 'Simple pricing. Pay once, no subscription.',
    pricingDesc: 'Try everything free with daily limits. When you are ready to plan seriously, get an Access Pass for a few days.',
    freeTitle: 'Free',
    freeItems: ['Quick & detailed estimates (daily limit)', 'Material calculators (daily limit)', 'Save projects with a free account (1 per day)'],
    getStarted: 'Get started',
    seePasses: 'See passes',
    faqTitle: 'Questions',
    faq: [
      ['Is the estimate a quotation?', 'No. It is a planning estimate based on typical Malaysian construction rates and standard material ratios. Always get quotations from licensed contractors before you build.'],
      ['What does the cost include?', 'Construction of the house: foundation, structure, walls, roof, ceiling, finishes, electrical and plumbing. Land, earthworks, piling, fencing, landscaping, built-in furniture, professional and permit fees are not included.'],
      ['Do I need an account?', 'No. You can try estimates and calculators without signing up. A free account lets you save projects and gives you more free uses each day.'],
    ],
    footer: 'RumahKira™ — house construction budgeting app',
    by: 'A digital estimation tool by Kondingau Community Insight (KCi)',
    disclaimer: 'Estimates are for planning only and are not a quotation.',
  },
  my: {
    signIn: 'Log masuk',
    openApp: 'Buka aplikasi',
    heroTitle: 'Ketahui kos rumah anda — sebelum membina',
    heroDesc: 'Anggar kos pembinaan dan bahan yang diperlukan untuk rumah kampung, teres atau banglo di Malaysia. Dalam beberapa minit, dalam Bahasa Malaysia atau English.',
    startEstimate: 'Mula anggaran percuma',
    calculators: 'Kalkulator bahan',
    trust: ['Percuma untuk dicuba', 'Tanpa pendaftaran', 'Bahasa Malaysia & English'],
    sampleLabel: 'Contoh anggaran',
    sampleHouse: 'Rumah teres · 1,400 kps · 1 tingkat · Standard',
    likely: 'Paling mungkin',
    range: 'Julat',
    materialsPreview: 'Termasuk senarai bahan penuh',
    waysTitle: 'Tiga cara merancang pembinaan anda',
    quickTitle: 'Anggaran pantas',
    quickDesc: 'Jawab 4 soalan — jenis rumah, saiz, tingkat dan kemasan — dan dapatkan julat kos dengan pecahan bajet.',
    detailedTitle: 'Anggaran terperinci',
    detailedDesc: 'Pilih dinding, asas, bumbung dan siling, kira bilik, pintu dan tingkap, dan dapatkan senarai bahan penuh.',
    calcTitle: `${ALL_CALCULATORS.length} kalkulator bahan`,
    calcDesc: 'Bata, simen, pasir, jubin, konkrit, bumbung dan lain-lain — dalam beg, biji, m³ dan tan yang boleh dipesan.',
    start: 'Mula',
    browse: 'Lihat',
    malaysiaTitle: 'Direka untuk membina di Malaysia',
    malaysiaDesc: 'Jenis rumah, tahap kemasan dan harga dalam RM mengikut cara rakyat Malaysia membina.',
    houseTypes: [
      ['kampung', 'Rumah kampung'],
      ['terrace', 'Rumah teres'],
      ['bungalow', 'Banglo'],
    ],
    categoriesTitle: 'Kira bahan untuk setiap bahagian rumah',
    pricingTitle: 'Harga mudah. Bayar sekali, tiada langganan.',
    pricingDesc: 'Cuba semua secara percuma dengan had harian. Bila sedia merancang dengan serius, dapatkan Pas Akses untuk beberapa hari.',
    freeTitle: 'Percuma',
    freeItems: ['Anggaran pantas & terperinci (had harian)', 'Kalkulator bahan (had harian)', 'Simpan projek dengan akaun percuma (1 sehari)'],
    getStarted: 'Mula sekarang',
    seePasses: 'Lihat pas',
    faqTitle: 'Soalan lazim',
    faq: [
      ['Adakah anggaran ini sebut harga?', 'Tidak. Ia anggaran perancangan berdasarkan kadar pembinaan biasa di Malaysia dan nisbah bahan standard. Sentiasa dapatkan sebut harga daripada kontraktor berlesen sebelum membina.'],
      ['Apa yang termasuk dalam kos?', 'Pembinaan rumah: asas, struktur, dinding, bumbung, siling, kemasan, elektrik dan paip. Tanah, kerja tanah, cerucuk, pagar, landskap, perabot terbina, fi profesional dan fi permit tidak termasuk.'],
      ['Perlukah saya mendaftar?', 'Tidak. Anda boleh cuba anggaran dan kalkulator tanpa mendaftar. Akaun percuma membolehkan anda simpan projek dan memberi lebih banyak penggunaan percuma setiap hari.'],
    ],
    footer: 'RumahKira™ — aplikasi belanjawan pembinaan rumah',
    by: 'Alat anggaran digital oleh Kondingau Community Insight (KCi)',
    disclaimer: 'Anggaran untuk perancangan sahaja dan bukan sebut harga.',
  },
};

const Section = ({ children, sx }) => (
  <Box component="section" sx={{ py: { xs: 6, md: 9 }, ...sx }}>
    <Container maxWidth="lg">{children}</Container>
  </Box>
);

const SampleResult = ({ t, language }) => {
  const sample = useMemo(
    () => calculateQuickEstimate({ houseType: 'terrace', builtUpArea: 1400, floors: 1, finishingLevel: 'standard' }),
    []
  );
  const my = language === 'my';
  const top = sample.summary.categories.slice(0, 4);
  const max = Math.max(...top.map((c) => c.percentage));
  const bricks = sample.materialGroups.flatMap((g) => g.items).find((i) => i.id === 'bricks');
  const cement = sample.materialGroups.flatMap((g) => g.items).find((i) => i.id === 'cement');

  return (
    <Card sx={{ boxShadow: (theme) => `0 20px 50px ${alpha(theme.palette.primary.main, 0.15)}` }}>
      <CardContent sx={{ p: 3 }}>
        <Chip size="small" label={t.sampleLabel} sx={{ mb: 1.5 }} />
        <Typography variant="body2" color="text.secondary">
          {t.sampleHouse}
        </Typography>
        <Typography className="tabular" sx={{ fontSize: '2.25rem', fontWeight: 800, letterSpacing: '-0.03em', mt: 1 }}>
          {formatCurrency(sample.summary.totalCost.avg)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t.likely} · {t.range} {formatCurrency(sample.summary.totalCost.min, { compact: true })} – {formatCurrency(sample.summary.totalCost.max, { compact: true })}
        </Typography>
        <Stack spacing={1.25} sx={{ mt: 2.5 }}>
          {top.map((c) => (
            <Box key={c.id}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" fontWeight={500}>
                  {my ? c.labelMy : c.label}
                </Typography>
                <Typography variant="caption" fontWeight={700} className="tabular">
                  {formatCurrency(c.amount, { compact: true })}
                </Typography>
              </Stack>
              <Box sx={{ mt: 0.5, height: 6, borderRadius: '3px', bgcolor: 'action.hover' }}>
                <Box sx={{ width: `${(c.percentage / max) * 100}%`, height: '100%', borderRadius: '3px', bgcolor: 'primary.main' }} />
              </Box>
            </Box>
          ))}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          {t.materialsPreview}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {[cement, bricks].filter(Boolean).map((i) => (
            <Chip
              key={i.id}
              size="small"
              variant="outlined"
              className="tabular"
              label={`${my ? i.labelMy : i.label}: ${formatNumber(i.avg)} ${my ? i.unitMy : i.unit}`}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

const WayCard = ({ icon, color, title, desc, cta, onClick }) => (
  <Card sx={{ height: '100%' }}>
    <ButtonBase onClick={onClick} sx={{ display: 'block', textAlign: 'left', height: '100%', width: '100%', p: 3 }}>
      <IconTile icon={icon} color={color} size={48} sx={{ mb: 2 }} />
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        {desc}
      </Typography>
      <Typography variant="body2" color="primary" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {cta} <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
      </Typography>
    </ButtonBase>
  </Card>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { plans } = useSubscription();
  const my = language === 'my';
  const t = text[language] || text.en;
  const paidPlans = (plans || []).filter((p) => p.plan_code !== 'free' && Number(p.price) > 0);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Top bar */}
      <Box
        component="header"
        sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1.25 }}>
            <BrandLogo />
            <Box sx={{ flex: 1 }} />
            <LanguageToggle />
            <ColorModeToggle />
            {user ? (
              <Button variant="contained" onClick={() => navigate('/app')}>
                {t.openApp}
              </Button>
            ) : (
              <Button variant="outlined" onClick={() => navigate('/auth')}>
                {t.signIn}
              </Button>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Section sx={{ pt: { xs: 5, md: 9 } }}>
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.1rem', sm: '2.75rem', md: '3.25rem' }, lineHeight: 1.1 }}
            >
              {t.heroTitle}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2.5, fontSize: { md: '1.1rem' }, maxWidth: 560 }}>
              {t.heroDesc}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
              <Button size="large" variant="contained" endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate('/app/estimate/quick')}>
                {t.startEstimate}
              </Button>
              <Button size="large" variant="outlined" onClick={() => navigate('/app/calculators')}>
                {t.calculators}
              </Button>
            </Stack>
            <Stack direction="row" spacing={2.5} sx={{ mt: 3, flexWrap: 'wrap', rowGap: 1 }}>
              {t.trust.map((item) => (
                <Stack key={item} direction="row" spacing={0.75} alignItems="center">
                  <CheckRoundedIcon color="success" sx={{ fontSize: 18 }} />
                  <Typography variant="body2" color="text.secondary">
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <SampleResult t={t} language={language} />
          </Grid>
        </Grid>
      </Section>

      {/* Ways to plan */}
      <Section sx={{ bgcolor: 'background.paper', borderBlock: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h2" sx={{ mb: 4, textAlign: { md: 'center' } }}>
          {t.waysTitle}
        </Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <WayCard icon={<BoltRoundedIcon />} color="primary" title={t.quickTitle} desc={t.quickDesc} cta={t.start} onClick={() => navigate('/app/estimate/quick')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <WayCard icon={<ListAltRoundedIcon />} color="secondary" title={t.detailedTitle} desc={t.detailedDesc} cta={t.start} onClick={() => navigate('/app/estimate/detailed')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <WayCard icon={<CalculateRoundedIcon />} color="success" title={t.calcTitle} desc={t.calcDesc} cta={t.browse} onClick={() => navigate('/app/calculators')} />
          </Grid>
        </Grid>
      </Section>

      {/* Made for Malaysia */}
      <Section>
        <Grid container spacing={5} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography variant="h4" component="h2">
              {t.malaysiaTitle}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
              {t.malaysiaDesc}
            </Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              {t.houseTypes.map(([id, label]) => (
                <Grid item xs={4} key={id}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2.5 } }}>
                      <Box sx={{ height: { xs: 44, sm: 70 }, mb: 1 }}>
                        <HouseIllustration type={id} />
                      </Box>
                      <Typography variant="subtitle2">{label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Typography variant="h6" component="h3" sx={{ mt: 7, mb: 2 }}>
          {t.categoriesTitle}
        </Typography>
        <Grid container spacing={1.5}>
          {CATEGORY_ORDER.map((catId) => (
            <Grid item xs={6} sm={4} md={4} lg={4} key={catId}>
              <Card>
                <ButtonBase
                  onClick={() => navigate(`/app/calculators?category=${catId}`)}
                  sx={{ width: '100%', justifyContent: 'flex-start', p: 1.5, gap: 1.5, textAlign: 'left' }}
                >
                  <IconTile icon={CATEGORY_ICONS[catId]} size={36} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {my ? CATEGORIES[catId].labelMy : CATEGORIES[catId].label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ display: { xs: 'none', sm: 'block' } }}>
                      {my ? CATEGORIES[catId].descriptionMy : CATEGORIES[catId].description}
                    </Typography>
                  </Box>
                </ButtonBase>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Pricing */}
      <Section sx={{ bgcolor: 'background.paper', borderBlock: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ maxWidth: 640, mb: 4 }}>
          <Typography variant="h4" component="h2">
            {t.pricingTitle}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
            {t.pricingDesc}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={paidPlans.length ? 3 : 6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle1">{t.freeTitle}</Typography>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 800 }}>RM0</Typography>
                <Stack spacing={0.75} sx={{ my: 2 }}>
                  {t.freeItems.map((i) => (
                    <Stack key={i} direction="row" spacing={1}>
                      <CheckRoundedIcon color="success" sx={{ fontSize: 18, mt: '1px' }} />
                      <Typography variant="body2">{i}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button fullWidth variant="outlined" onClick={() => navigate('/app')}>
                  {t.getStarted}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          {paidPlans.map((plan) => (
            <Grid item xs={12} sm={6} md={3} key={plan.plan_code}>
              <Card sx={{ height: '100%', borderColor: (plan.is_recommended || plan.plan_code === 'planning_pass') ? 'primary.main' : 'divider' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1">{my ? plan.name_my || plan.name_en : plan.name_en}</Typography>
                  <Stack direction="row" alignItems="baseline" spacing={0.75}>
                    <Typography sx={{ fontSize: '1.75rem', fontWeight: 800 }}>RM{Number(plan.price)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      / {my ? plan.duration_text_my : plan.duration_text_en}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.75} sx={{ my: 2 }}>
                    {getPlanBenefits(plan, language).map((b) => (
                      <Stack key={b} direction="row" spacing={1}>
                        <CheckRoundedIcon color="success" sx={{ fontSize: 18, mt: '1px' }} />
                        <Typography variant="body2">{b}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Button fullWidth variant={(plan.is_recommended || plan.plan_code === 'planning_pass') ? 'contained' : 'outlined'} onClick={() => navigate('/app/account#passes')}>
                    {t.seePasses}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* FAQ */}
      <Section>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography variant="h4" component="h2">
              {t.faqTitle}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack spacing={1}>
              {t.faq.map(([q, a]) => (
                <Accordion key={q}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Typography fontWeight={600}>{q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      {a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Section>

      {/* Final call to action + footer */}
      <Box component="footer" sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', py: 5 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box>
              <BrandLogo />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t.footer}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {t.by}
              </Typography>
              <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 1 }}>
                {t.disclaimer}
              </Typography>
            </Box>
            <Button size="large" variant="contained" endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate('/app/estimate/quick')}>
              {t.startEstimate}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
